(() => {
  if (customElements.get('deryan-scratch-signup')) return;

  class DeryanScratchSignup extends HTMLElement {
    connectedCallback() {
      if (this.abort && !this.abort.signal.aborted) return;
      this.abort = new AbortController();
      this.launcher = this.querySelector('[data-open]');
      this.demo = this.dataset.demo === 'true';
      this.storageKey = 'deryan-scratch-v1-' + (this.dataset.campaign || 'welcome');
      this.state = this.readState();
      this.listen(this.launcher, 'click', () => this.open());
      const forced = new URLSearchParams(location.search).get('deryan_scratch_preview') === '1';
      const editor = Boolean(window.Shopify?.designMode);
      if (forced || (editor && this.dataset.editorPreview === 'true')) {
        this.timer = setTimeout(() => this.open(), 500);
      } else if (this.dataset.subscribed !== 'true' && !this.state.subscribed && !editor) {
        this.launcher.hidden = false;
        if (!(this.state.until > Date.now())) this.timer = setTimeout(() => this.autoOpen(), Number(this.dataset.delay) * 1000);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.revealTimer);
      this.abort?.abort();
      this.requestAbort?.abort();
      if (this.dialog?.open) this.close();
    }

    listen(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.abort.signal });
    }

    readState() {
      if (this.demo) return {};
      try { return JSON.parse(localStorage.getItem(this.storageKey)) || {}; }
      catch { return {}; }
    }

    saveState(changes) {
      this.state = { ...this.state, ...changes };
      if (this.demo) return;
      try { localStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch { /* Storage is optional. */ }
    }

    autoOpen() {
      if (!this.isConnected || this.state.subscribed || this.state.until > Date.now()) return;
      const occupied = Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some(element => {
        if (element === this.dialog || element.getAttribute('aria-hidden') === 'true') return false;
        const bounds = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        return bounds.width > 0 && bounds.height > 0 && bounds.right > 0 && bounds.bottom > 0 && bounds.left < innerWidth && bounds.top < innerHeight && style.visibility !== 'hidden' && Number(style.opacity) > 0;
      });
      if (document.hidden || occupied || document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) {
        this.timer = setTimeout(() => this.autoOpen(), 10000);
        return;
      }
      this.open();
    }

    open() {
      clearTimeout(this.timer);
      if (!this.dialog) this.mount();
      if (this.dialog.open || !this.dialog.showModal) return;
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      this.scrollY = window.scrollY;
      this.bodyStyles = {};
      for (const key of ['position', 'top', 'left', 'width']) this.bodyStyles[key] = document.body.style[key];
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      Object.assign(document.body.style, { position: 'fixed', top: `-${this.scrollY}px`, left: '0', width: '100%' });
      this.launcher.hidden = true;
      this.saveState({ until: Date.now() + Number(this.dataset.dismissDays) * 86400000 });
    }

    mount() {
      this.append(this.querySelector('template').content.cloneNode(true));
      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('canvas');
      this.card = this.querySelector('[data-card]');
      this.intro = this.querySelector('[data-intro]');
      this.signup = this.querySelector('[data-signup]');
      this.success = this.querySelector('[data-success]');
      this.form = this.querySelector('form');
      this.querySelectorAll('[data-close]').forEach(button => this.listen(button, 'click', () => this.close()));
      this.listen(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      this.listen(this.dialog, 'click', event => {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) this.close();
      });
      this.listen(this.querySelector('[data-reveal]'), 'click', () => this.reveal());
      this.listen(this.form, 'submit', event => this.subscribe(event));
      this.listen(this.querySelector('[data-copy]'), 'click', async () => {
        try {
          await navigator.clipboard.writeText(this.querySelector('[data-code]').textContent.trim());
          this.querySelector('[data-copy-status]').textContent = this.dataset.copied;
        } catch {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(this.querySelector('[data-code]'));
          selection.removeAllRanges(); selection.addRange(range);
        }
      });
      this.paint();
      this.listen(this.canvas, 'pointerdown', event => {
        if (this.revealed || (event.pointerType === 'mouse' && event.button !== 0)) return;
        event.preventDefault();
        this.pointerId = event.pointerId;
        this.canvas.setPointerCapture(event.pointerId);
        this.lastPoint = this.point(event);
        this.card.classList.add('is-scratching');
        this.erase(this.lastPoint, this.lastPoint);
      });
      this.listen(this.canvas, 'pointermove', event => {
        if (event.pointerId !== this.pointerId || this.revealed) return;
        const point = this.point(event);
        this.erase(this.lastPoint, point);
        this.lastPoint = point;
        this.checkProgress();
      });
      const stop = event => {
        if (event.pointerId !== this.pointerId) return;
        if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
        this.pointerId = null;
        this.checkProgress(true);
      };
      this.listen(this.canvas, 'pointerup', stop);
      this.listen(this.canvas, 'pointercancel', stop);
      this.listen(this.canvas, 'lostpointercapture', () => { this.pointerId = null; });
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      document.documentElement.style.overflow = this.previousOverflow || '';
      Object.assign(document.body.style, this.bodyStyles);
      window.scrollTo({ top: this.scrollY, behavior: 'instant' });
      this.launcher.hidden = this.state.subscribed || this.dataset.subscribed === 'true';
      if (this.previousFocus?.isConnected && this.previousFocus !== document.body) this.previousFocus.focus({ preventScroll: true });
      else if (!this.launcher.hidden) this.launcher.focus({ preventScroll: true });
    }

    paint() {
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      if (!this.ctx) return;
      const ctx = this.ctx;
      const { width, height } = this.canvas;
      ctx.fillStyle = this.dataset.coating || '#b8baa9'; ctx.fillRect(0, 0, width, height);
      const glow = ctx.createLinearGradient(0, 0, width, height);
      glow.addColorStop(0, '#ffffff40'); glow.addColorStop(.3, '#ffffff05'); glow.addColorStop(.55, '#fff6c84d'); glow.addColorStop(.75, '#59420f12'); glow.addColorStop(1, '#ffffff26');
      ctx.fillStyle = glow; ctx.fillRect(0, 0, width, height);
      let seed = 17;
      const random = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
      for (let i = 0; i < 20000; i++) {
        ctx.fillStyle = random() > .5 ? '#ffffff20' : '#14200f16';
        ctx.fillRect(random() * width, random() * height, 1.5, 1.5);
      }

    }

    point(event) {
      const bounds = this.canvas.getBoundingClientRect();
      return { x: (event.clientX - bounds.left) * this.canvas.width / bounds.width, y: (event.clientY - bounds.top) * this.canvas.height / bounds.height };
    }

    erase(from, to) {
      if (!this.ctx) return;
      const ctx = this.ctx;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = 1;
      ctx.strokeStyle = '#000';
      ctx.fillStyle = '#000';
      ctx.lineWidth = 62; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
      ctx.beginPath(); ctx.arc(to.x, to.y, 31, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }

    checkProgress(force = false) {
      if (!this.ctx || this.revealed || (!force && performance.now() - (this.lastCheck || 0) < 100)) return;
      this.lastCheck = performance.now();
      const { width, height } = this.canvas;
      const pixels = this.ctx.getImageData(0, 0, width, height).data;
      let clear = 0; let total = 0;
      for (let y = 10; y < height; y += 15) {
        for (let x = 10; x < width; x += 15) { total++; if (pixels[(y * width + x) * 4 + 3] < 100) clear++; }
      }
      if (clear / total >= .36) this.reveal();
    }

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      this.card.classList.add('is-revealed');
      this.revealTimer = setTimeout(() => {
        this.intro.hidden = true;
        this.signup.hidden = false;
        this.dialog.classList.add('is-signup');
        this.dialog.setAttribute('aria-labelledby', this.querySelector('[data-reward-title]').id);
        if (this.dialog.open) this.querySelector('[data-reward-title]').focus({ preventScroll: true });
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650);
    }

    signupLanguageProperties() {
      // Capture the page language at submission, not the visitor's country or browser preference.
      // Keep this separate from native locale so repeat signups cannot overwrite an email preference.
      const supported = ['nl', 'en', 'de', 'fr', 'es', 'it', 'da', 'sv', 'pl', 'no'];
      const languageOf = value => {
        if (typeof value !== 'string') return '';
        const tag = value.trim().toLowerCase().replace(/_/g, '-');
        if (!/^[a-z]{2}(?:-[a-z]{2})?$/.test(tag)) return '';
        const language = ['nb', 'nn'].includes(tag.split('-')[0]) ? 'no' : tag.split('-')[0];
        return supported.includes(language) ? language : '';
      };
      let language = '';
      let source = '';
      try {
        if (window.Weglot?.initialized !== false && typeof window.Weglot?.getCurrentLang === 'function') {
          language = languageOf(window.Weglot.getCurrentLang());
          if (language) source = 'weglot';
        }
      } catch { /* Translation availability must never block signup. */ }
      if (!language) {
        const host = location.hostname.toLowerCase();
        const translatedHost = host.match(/^([a-z]{2})\.deryan\.com$/);
        if (translatedHost) {
          language = languageOf(translatedHost[1]);
          if (language) source = 'storefront_subdomain';
        }
        if (!language) {
          language = languageOf(document.documentElement.lang);
          if (language) source = 'document_language';
        }
        if (!language) {
          language = languageOf(window.Shopify?.locale);
          if (language) source = 'shopify_locale';
        }
        if (!language && (host === 'www.deryan.com' || host === 'deryan.com')) {
          language = 'nl';
          source = 'storefront_default';
        }
      }
      return language ? {
        DERYAN_signup_language: language,
        DERYAN_signup_language_source: source,
        DERYAN_signup_language_captured_at: new Date().toISOString()
      } : {};
    }

    async subscribe(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      const email = this.form.elements.email.value.trim();
      const button = this.form.querySelector('[type="submit"]');
      const error = this.querySelector('[data-error-message]');
      const originalLabel = button.innerHTML;
      this.busy = true; button.disabled = true; button.textContent = this.dataset.busy; error.hidden = true;
      this.requestAbort = new AbortController();
      const timeout = setTimeout(() => this.requestAbort.abort(), 20000);
      try {
        // Preview never sends an address or creates a subscriber.
        if (this.demo) {
          this.completeSignup();
          return;
        }
        const code = this.querySelector('[data-code]').textContent.trim();
        if (!code || !this.dataset.company || !this.dataset.list) throw new Error('configuration');
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.company)}`, {
          method: 'POST', credentials: 'omit', signal: this.requestAbort.signal,
          headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-10-15' },
          body: JSON.stringify({ data: {
            type: 'subscription',
            attributes: { custom_source: 'DERYAN | Kraskaart', profile: { data: { type: 'profile', attributes: {
              email,
              properties: { '$source': 'DERYAN | Kraskaart', DERYAN_signup_experience: 'scratch', ...this.signupLanguageProperties() },
              subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
            } } } },
            relationships: { list: { data: { type: 'list', id: this.dataset.list } } }
          } })
        });
        if (!response.ok) throw new Error(response.status === 429 ? 'rate' : 'request');
        this.completeSignup();
      } catch (failure) {
        error.textContent = failure.message === 'rate' ? this.dataset.rateError : this.dataset.error;
        error.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.busy = false; button.disabled = false; button.innerHTML = originalLabel;
      }
    }

    completeSignup() {
      this.saveState({ subscribed: true });
      this.launcher.hidden = true;
      this.signup.hidden = true;
      this.success.hidden = false;
      this.form.reset();
      const heading = this.querySelector('[data-success-title]');
      this.dialog.setAttribute('aria-labelledby', heading.id);
      if (this.dialog.open) heading.focus({ preventScroll: true });
    }
  }
  customElements.define('deryan-scratch-signup', DeryanScratchSignup);
})();
